import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, MoreHorizontal, Filter, Key, Calendar, CheckCircle, XCircle, Copy, Eye } from 'lucide-react';
import { getAccessCodes, createAccessCode, updateAccessCode, deleteAccessCode } from '../utils/accessCodeApi';
import { mockAccessCodes, accessCodeStatuses } from '../utils/mockAccessCodeData';
import { useAuth } from '../contexts/AuthContext';

const AccessCodes = () => {
  const navigate = useNavigate();
  const { user, userSites } = useAuth();
  const [accessCodes, setAccessCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showModal, setShowModal] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [editingAccessCode, setEditingAccessCode] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error2, setError2] = useState('');
  const [successMessage2, setSuccessMessage2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitting2, setSubmitting2] = useState(false);
  // Get site_id from user login data
  const currentSiteId = userSites?.site_id || user?.site_id || null;
  
  const [formData, setFormData] = useState({
    value: '',
    code: '',
    quantity: '',
    status: 'active',
    created_at: '',
    unlimited: false,
    site_id: currentSiteId
  });

  const [formData2, setFormData2] = useState({
    value: '',
    code: '',
    quantity: '',
    status: 'active',
    created_at: '',
    unlimited: false,
    site_id: currentSiteId,
    parent_id: ''
  });

  // Ensure accessCodes is always an array
  const safeAccessCodes = Array.isArray(accessCodes) ? accessCodes : [];

  // Dynamic column configuration based on API response
  const getTableColumns = () => {
    if (safeAccessCodes.length === 0) return [];
    
    const sampleAccessCode = safeAccessCodes[0];
    const columns = [];
    
    const getFieldValue = (accessCode, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (accessCode[name] !== undefined && accessCode[name] !== null && accessCode[name] !== '') {
          return accessCode[name];
        }
      }
      return defaultValue;
    };
    
    // Always include actions as first column
    columns.push({
      key: 'actions',
      label: 'Actions',
      type: 'actions',
      width: 'w-32'
    });

    // Always include value column
    columns.push({
      key: 'parent_value',
      label: 'Company',
      type: 'text',
      width: 'w-32'
    });
    
    // Always include value column
    columns.push({
      key: 'value',
      label: 'Description',
      type: 'text',
      width: 'w-32'
    });
    
    // Always include code column
    columns.push({
      key: 'code',
      label: 'Access Code',
      type: 'code',
      width: 'w-24'
    });
    
    // Always include quantity column
    columns.push({
      key: 'quantity',
      label: 'Quantity',
      type: 'number',
      width: 'w-24'
    });
    
    // Always include status column
    columns.push({
      key: 'status',
      label: 'Status',
      type: 'badge',
      width: 'w-24'
    });
    
    return columns;
  };

  const tableColumns = getTableColumns();

  // Render table cell based on column type
  const renderTableCell = (column, accessCode) => {
    const getFieldValue = (accessCode, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (accessCode[name] !== undefined && accessCode[name] !== null && accessCode[name] !== '') {
          return accessCode[name];
        }
      }
      return defaultValue;
    };

    switch (column.type) {
      
      case 'code':
        const code = getFieldValue(accessCode, ['code']);
        return (
          <div className="flex items-center">
            <Key className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-sm font-mono font-medium text-gray-900">{code}</span>
          </div>
        );
      
      case 'badge':
        if (column.key === 'status') {
          const status = getFieldValue(accessCode, ['status']);
          return getStatusBadge(status);
        }
        return null;
      
      case 'number':
        const numberValue = getFieldValue(accessCode, [column.key]);
        return (
          <span className="text-sm font-medium text-gray-900">
            {numberValue || 0}
          </span>
        );
      
      case 'usage':
        const usageLimit = getFieldValue(accessCode, ['usage_limit']);
        const usageCount = getFieldValue(accessCode, ['usage_count']);
        const usagePercentage = usageLimit ? Math.round((usageCount / usageLimit) * 100) : 0;
        return (
          <div className="text-sm">
            <div className="flex justify-between">
              <span className="text-gray-900">{usageCount || 0}</span>
              <span className="text-gray-500">/ {usageLimit || '∞'}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div 
                className={`h-2 rounded-full ${usagePercentage >= 90 ? 'bg-red-500' : usagePercentage >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              ></div>
            </div>
          </div>
        );
      

      
      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            {/**<button
              onClick={() => handleCopyCode(accessCode.code)}
              className="text-blue-600 hover:text-blue-900"
              title="Copy Code"
            >
              <Copy className="h-4 w-4" />
            </button>*/}
            <button
              onClick={() => navigate(`/admin/access-codes/${accessCode.id}`)}
              className="text-blue-600 hover:text-blue-900"
              title="View Surveys"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleEdit(accessCode)}
              className="text-primary-600 hover:text-primary-900"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDelete(accessCode.id)}
              className="text-red-600 hover:text-red-900"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            {/**<button className="text-gray-400 hover:text-gray-600" title="More">
              <MoreHorizontal className="h-4 w-4" />
            </button>*/}
          </div>
        );
      
      default:
        // Special handling for parent_value to access parent.value
        if (column.key === 'parent_value') {
          const parentValue = accessCode.parent?.value || accessCode.parent_value || null;
          return (
            <span className="text-sm text-gray-500">
              {parentValue || `No ${column.label}`}
            </span>
          );
        }
        const textValue = getFieldValue(accessCode, [column.key]);
        return (
          <span className="text-sm text-gray-500">
            {textValue || `No ${column.label}`}
          </span>
        );
    }
  };

  // Fetch access codes on component mount
  useEffect(() => {
    fetchAccessCodes();
  }, []);

  // Update site_id in formData when currentSiteId changes
  useEffect(() => {
    if (currentSiteId) {
      setFormData(prev => ({ ...prev, site_id: currentSiteId }));
      setFormData2(prev => ({ ...prev, site_id: currentSiteId }));
    }
  }, [currentSiteId]);

  const fetchAccessCodes = async () => {
    setLoading(true);
    try {
      const response = await getAccessCodes();
      setApiResponse(response);
      
      if (response.success) {
        let accessCodeData = response.data;
        
        // Handle different possible API response structures
        if (response.data && response.data.data) {
          accessCodeData = response.data.data;
        } else if (response.data && response.data.access_codes) {
          accessCodeData = response.data.access_codes;
        } else if (response.data && response.data.items) {
          accessCodeData = response.data.items;
        }
        
        if (Array.isArray(accessCodeData)) {
          setAccessCodes(accessCodeData);
        } else {
          setAccessCodes([]);
        }
      } else {
        setAccessCodes([]);
      }
    } catch (error) {
      setAccessCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) {
      console.log('⚠️ Form submission already in progress, ignoring duplicate call');
      return;
    }
    
    setSubmitting(true);
    setError('');
    setSuccessMessage('');
    
    const submitData = { ...formData };
    // If unlimited is checked, set quantity to null or empty string
    if (submitData.unlimited) {
      submitData.quantity = null;
    }
    // Ensure site_id is included
    submitData.site_id = currentSiteId;
    if (!editingAccessCode) {
      submitData.created_at = new Date().toISOString();
    }

    try {
      let response;
      if (editingAccessCode) {
        response = await updateAccessCode(editingAccessCode.id, submitData);
      } else {
        response = await createAccessCode(submitData);
      }

      if (response.success) {
        setSuccessMessage(editingAccessCode ? 'Access code updated successfully!' : 'Access code created successfully!');
        setTimeout(() => {
          setShowModal(false);
          setEditingAccessCode(null);
          resetForm();
          fetchAccessCodes();
        }, 1500);
      } else {
        // Extract errors from API response
        let errorMessage = '';
        
        // Check for response.errors.code (direct errors object)
        if (response.errors && response.errors.code) {
          const codeErrors = response.errors.code;
          if (Array.isArray(codeErrors)) {
            errorMessage = codeErrors.join('\n');
          } else {
            errorMessage = String(codeErrors);
          }
        }
        // Check for response.errors (general errors object)
        else if (response.errors) {
          const errors = response.errors;
          // Handle object with field-specific errors
          if (typeof errors === 'object' && !Array.isArray(errors)) {
            const errorMessages = [];
            Object.keys(errors).forEach(field => {
              if (Array.isArray(errors[field])) {
                errorMessages.push(...errors[field]);
              } else {
                errorMessages.push(errors[field]);
              }
            });
            errorMessage = errorMessages.join('\n');
          } else if (Array.isArray(errors)) {
            errorMessage = errors.join('\n');
          } else {
            errorMessage = String(errors);
          }
        }
        // Check for validation errors in data (common Laravel format)
        else if (response.data && response.data.errors) {
          const errors = response.data.errors;
          // Handle object with field-specific errors
          if (typeof errors === 'object' && !Array.isArray(errors)) {
            const errorMessages = [];
            Object.keys(errors).forEach(field => {
              if (Array.isArray(errors[field])) {
                errorMessages.push(...errors[field]);
              } else {
                errorMessages.push(errors[field]);
              }
            });
            errorMessage = errorMessages.join('\n');
          } else if (Array.isArray(errors)) {
            errorMessage = errors.join('\n');
          } else {
            errorMessage = String(errors);
          }
        } 
        // Check for error message in data
        else if (response.data && response.data.message) {
          errorMessage = response.data.message;
        }
        // Check for error in response
        else if (response.error) {
          errorMessage = response.error;
        }
        // Check for message in response
        else if (response.message) {
          errorMessage = response.message;
        }
        // Fallback
        else {
          errorMessage = 'Failed to save access code. Please try again.';
        }
        
        setError(errorMessage);
        console.error('API error response:', response);
        console.error('Extracted error message:', errorMessage);
      }
    } catch (error) {
      // Show error message from exception
      const errorMessage = error.message || 'An error occurred while saving the access code. Please try again.';
      setError(errorMessage);
      console.error('Error submitting access code:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit2 = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting2) {
      console.log('⚠️ Form submission already in progress, ignoring duplicate call');
      return;
    }
    
    setSubmitting2(true);
    setError2('');
    setSuccessMessage2('');
    
    const submitData = { ...formData2 };
    // If unlimited is checked, set quantity to null or empty string
    if (submitData.unlimited) {
      submitData.quantity = null;
    }
    // Ensure site_id is included
    submitData.site_id = currentSiteId;
    submitData.created_at = new Date().toISOString();

    try {
      const response = await createAccessCode(submitData);

      if (response.success) {
        setSuccessMessage2('Access code created successfully!');
        setTimeout(() => {
          setShowModal2(false);
          resetForm2();
          fetchAccessCodes();
        }, 1500);
      } else {
        // Extract errors from API response
        let errorMessage = '';
        
        // Check for response.errors.code (direct errors object)
        if (response.errors && response.errors.code) {
          const codeErrors = response.errors.code;
          if (Array.isArray(codeErrors)) {
            errorMessage = codeErrors.join('\n');
          } else {
            errorMessage = String(codeErrors);
          }
        }
        // Check for response.errors (general errors object)
        else if (response.errors) {
          const errors = response.errors;
          // Handle object with field-specific errors
          if (typeof errors === 'object' && !Array.isArray(errors)) {
            const errorMessages = [];
            Object.keys(errors).forEach(field => {
              if (Array.isArray(errors[field])) {
                errorMessages.push(...errors[field]);
              } else {
                errorMessages.push(errors[field]);
              }
            });
            errorMessage = errorMessages.join('\n');
          } else if (Array.isArray(errors)) {
            errorMessage = errors.join('\n');
          } else {
            errorMessage = String(errors);
          }
        }
        // Check for validation errors in data (common Laravel format)
        else if (response.data && response.data.errors) {
          const errors = response.data.errors;
          // Handle object with field-specific errors
          if (typeof errors === 'object' && !Array.isArray(errors)) {
            const errorMessages = [];
            Object.keys(errors).forEach(field => {
              if (Array.isArray(errors[field])) {
                errorMessages.push(...errors[field]);
              } else {
                errorMessages.push(errors[field]);
              }
            });
            errorMessage = errorMessages.join('\n');
          } else if (Array.isArray(errors)) {
            errorMessage = errors.join('\n');
          } else {
            errorMessage = String(errors);
          }
        } 
        // Check for error message in data
        else if (response.data && response.data.message) {
          errorMessage = response.data.message;
        }
        // Check for error in response
        else if (response.error) {
          errorMessage = response.error;
        }
        // Check for message in response
        else if (response.message) {
          errorMessage = response.message;
        }
        // Fallback
        else {
          errorMessage = 'Failed to save access code. Please try again.';
        }
        
        setError2(errorMessage);
        console.error('API error response:', response);
        console.error('Extracted error message:', errorMessage);
      }
    } catch (error) {
      // Show error message from exception
      const errorMessage = error.message || 'An error occurred while saving the access code. Please try again.';
      setError2(errorMessage);
      console.error('Error submitting access code:', error);
    } finally {
      setSubmitting2(false);
    }
  };

  const handleEdit = (accessCode) => {
    setEditingAccessCode(accessCode);
    setFormData({
      value: accessCode.value || '',
      code: accessCode.code || '',
      quantity: accessCode.quantity || '',
      status: accessCode.status || 'active',
      created_at: accessCode.created_at || '',
      unlimited: accessCode.unlimited || accessCode.quantity === null || accessCode.quantity === '' || accessCode.quantity === 0,
      site_id: accessCode.site_id || currentSiteId
    });
    setShowModal(true);
  };

  const handleDelete = async (accessCodeId) => {
    if (window.confirm('Are you sure you want to delete this access code?')) {
      try {
        const response = await deleteAccessCode(accessCodeId);
        if (response.success) {
          fetchAccessCodes();
        } else {
          setAccessCodes(prev => prev.filter(accessCode => accessCode.id !== accessCodeId));
        }
      } catch (error) {
        setAccessCodes(prev => prev.filter(accessCode => accessCode.id !== accessCodeId));
      }
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    // You could add a toast notification here
  };

  const resetForm = () => {
    setFormData({
      value: '',
      code: '',
      quantity: '',
      status: 'active',
      created_at: '',
      unlimited: false,
      site_id: currentSiteId
    });
    setError('');
    setSuccessMessage('');
  };

  const resetForm2 = () => {
    setFormData2({
      value: '',
      code: '',
      quantity: '',
      status: 'active',
      created_at: '',
      unlimited: false,
      site_id: currentSiteId,
      parent_id: ''
    });
    setError2('');
    setSuccessMessage2('');
  };

  const openCreateModal = () => {
    setEditingAccessCode(null);
    resetForm();
    setShowModal(true);
  };

  const openCreateModalDivision = () => {
    setEditingAccessCode(null);
    resetForm2();
    setShowModal2(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAccessCode(null);
    resetForm();
    setError('');
    setSuccessMessage('');
  };

  const closeModal2 = () => {
    setShowModal2(false);
    setEditingAccessCode(null);
    resetForm2();
    setError2('');
    setSuccessMessage2('');
  };

  const filteredAccessCodes = searchTerm ? safeAccessCodes.filter(accessCode => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (accessCode.value && accessCode.value.toLowerCase().includes(searchLower)) ||
      (accessCode.code && accessCode.code.toLowerCase().includes(searchLower)) ||
      (accessCode.quantity && accessCode.quantity.toString().includes(searchLower)) ||
      (accessCode.status && accessCode.status.toLowerCase().includes(searchLower))
    );
  }) : safeAccessCodes;



  // Client-side Pagination (table only)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const totalItems = filteredAccessCodes.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const clampedPage = Math.min(currentPage, totalPages);
  const pageStartIndex = totalItems === 0 ? 0 : (clampedPage - 1) * pageSize;
  const pageEndIndexExclusive = Math.min(pageStartIndex + pageSize, totalItems);
  const paginatedAccessCodes = filteredAccessCodes.slice(pageStartIndex, pageEndIndexExclusive);

  // If data shrinks, keep the page number in range.
  useEffect(() => {
    if (currentPage !== clampedPage) setCurrentPage(clampedPage);
  }, [clampedPage, currentPage]);

  const getStatusBadge = (status) => {
    const statusConfig = accessCodeStatuses.find(s => s.value === status) || accessCodeStatuses[1];
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Access Codes</h1>
          <p className="text-gray-600">Manage access codes and their permissions.</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
        <button 
          onClick={openCreateModal}
            className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
            <span>Add Company Code</span>
          </button>

          <button 
            onClick={openCreateModalDivision}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Division Code</span>
        </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search access codes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          {/**<div className="flex items-center space-x-3">
            <button className="btn-secondary flex items-center space-x-2">
              <Filter className="h-4 w-4" />
              <span>Filter</span>
            </button>
            
          </div>*/}
        </div>
      </div>

      {/* Access Codes Table */}
      <div className="card">
        {/* Table Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Access Codes List</h3>
              {/**<p className="text-sm text-gray-600">
                {apiResponse && apiResponse.success 
                  ? `Showing ${safeAccessCodes.length} access codes `//from API with ${tableColumns.length} columns` 
                  : `Showing ${safeAccessCodes.length} access codes with ${tableColumns.length} columns (${safeAccessCodes.length > 0 ? 'from fallback data' : 'no data available'})`
                }
              </p>*/}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto table-container">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {tableColumns.map((column) => (
                  <th 
                    key={column.key} 
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeAccessCodes.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <Key className="h-12 w-12 text-gray-300" />
                      <p className="text-lg font-medium">No access codes found</p>
                      {/**<p className="text-sm">
                        {apiResponse && apiResponse.success 
                          ? 'No access codes found' 
                          : 'No access codes found'
                        }
                      </p>*/}
                      <button
                        onClick={fetchAccessCodes}
                        className="btn-secondary mt-2"
                      >
                        Try Again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : filteredAccessCodes.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <Search className="h-12 w-12 text-gray-300" />
                      <p className="text-lg font-medium">No access codes match your search</p>
                      <p className="text-sm">Try adjusting your search terms</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedAccessCodes.map((accessCode) => (
                  <tr key={accessCode.id} className="hover:bg-gray-50">
                    {tableColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderTableCell(column, accessCode)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        {filteredAccessCodes.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <span>Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-3">
                <div className="text-sm text-gray-600">
                  Page {clampedPage} of {totalPages}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={clampedPage <= 1}
                    className={`px-3 py-1 rounded-md border text-sm ${
                      clampedPage <= 1
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Prev
                  </button>

                  {(() => {
                    const windowSize = 5;
                    const half = Math.floor(windowSize / 2);
                    const start = Math.max(1, clampedPage - half);
                    const end = Math.min(totalPages, start + windowSize - 1);
                    const adjustedStart = Math.max(1, end - windowSize + 1);
                    const pages = [];
                    for (let p = adjustedStart; p <= end; p += 1) pages.push(p);

                    return pages.map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1 rounded-md border text-sm ${
                          p === clampedPage
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    ));
                  })()}

                  <button
                    type="button"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={clampedPage >= totalPages}
                    className={`px-3 py-1 rounded-md border text-sm ${
                      clampedPage >= totalPages
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Access Code Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={closeModal}
        >
          <div
            className="relative top-[10px] mx-auto p-5 border w-96 shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingAccessCode ? 'Edit Access Code' : 'Create Company Code'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
                  {successMessage}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <XCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-red-800">
                        {error.includes('\n') ? (
                          error.split('\n').map((line, index) => (
                            <div key={index}>{line}</div>
                          ))
                        ) : (
                          error
                        )}
                      </h3>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Description
                     </label>
                     <input
                       type="text"
                       required
                       value={formData.value}
                       onChange={(e) => {
                         setFormData({...formData, value: e.target.value});
                         if (error) setError('');
                       }}
                       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                       placeholder="Name of Company"
                     />
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Code
                     </label>
                     <input
                       type="text"
                       required
                       value={formData.code}
                       onChange={(e) => {
                         setFormData({...formData, code: e.target.value});
                         if (error) setError('');
                       }}
                       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                       placeholder="AC001"
                     />
                   </div>
                 </div>
                 
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Quantity
                     </label>
                     <input
                       type="number"
                       required={!formData.unlimited}
                       value={formData.unlimited ? '0' : formData.quantity}
                       onChange={(e) => {
                         if (!formData.unlimited) {
                         setFormData({...formData, quantity: e.target.value});
                         if (error) setError('');
                         }
                       }}
                       readOnly={formData.unlimited}
                       className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                         formData.unlimited ? 'bg-gray-100 cursor-not-allowed' : ''
                       }`}
                       placeholder={formData.unlimited ? '0' : '100'}
                     />
                     <div className="mt-2 flex items-center">
                      <input
                        type="checkbox"
                        id="unlimited"
                        checked={formData.unlimited && formData.unlimited !== 1}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            unlimited: e.target.checked,
                            quantity: e.target.checked ? '' : formData.quantity
                          });
                          if (error) setError('');
                        }}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                       <label htmlFor="unlimited" className="ml-2 block text-sm text-gray-700">
                         Unlimited
                       </label>
                     </div>
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Status
                     </label>
                     <select
                       value={formData.status}
                       onChange={(e) => setFormData({...formData, status: e.target.value})}
                       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     >
                       {accessCodeStatuses.map(status => (
                         <option key={status.value} value={status.value}>
                           {status.label}
                         </option>
                       ))}
                     </select>
                   </div>
                 </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : (editingAccessCode ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Second Modal - Division Code Modal */}
      {showModal2 && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={closeModal2}
        >
          <div
            className="relative top-[10px] mx-auto p-5 border w-96 shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Create Division Code
                </h3>
                <button
                  onClick={closeModal2}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              {/* Success Message */}
              {successMessage2 && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
                  {successMessage2}
                </div>
              )}

              {/* Error Message */}
              {error2 && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <XCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-red-800">
                        {error2.includes('\n') ? (
                          error2.split('\n').map((line, index) => (
                            <div key={index}>{line}</div>
                          ))
                        ) : (
                          error2
                        )}
                      </h3>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit2} className="space-y-4">
                 <div className="grid grid-cols-1 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Select Company Code
                     </label>
                     <select
                       value={formData2.parent_id}
                       onChange={(e) => {
                         setFormData2({...formData2, parent_id: e.target.value});
                         if (error2) setError2('');
                       }}
                       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     >
                       <option value="">-- Select Access Code --</option>
                       {safeAccessCodes
                         .filter((accessCode) => {
                           const codeSiteId = accessCode.site_id || accessCode.siteId;
                           return currentSiteId && codeSiteId && codeSiteId.toString() === currentSiteId.toString();
                         })
                         .map((accessCode) => (
                           <option key={accessCode.id} value={accessCode.id}>
                             {accessCode.code} - {accessCode.value || 'N/A'}
                           </option>
                         ))}
                     </select>
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Description
                     </label>
                     <input
                       type="text"
                       required
                       value={formData2.value}
                       onChange={(e) => {
                         setFormData2({...formData2, value: e.target.value});
                         if (error2) setError2('');
                       }}
                       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                       placeholder="Name of Division"
                     />
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Code
                     </label>
                     <input
                       type="text"
                       required
                       value={formData2.code}
                       onChange={(e) => {
                         setFormData2({...formData2, code: e.target.value});
                         if (error2) setError2('');
                       }}
                       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                       placeholder="AC001"
                     />
                   </div>
                 </div>
                 
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Quantity
                     </label>
                     <input
                       type="number"
                       required={!formData2.unlimited}
                       value={formData2.unlimited ? '0' : formData2.quantity}
                       onChange={(e) => {
                         if (!formData2.unlimited) {
                           setFormData2({...formData2, quantity: e.target.value});
                           if (error2) setError2('');
                         }
                       }}
                       readOnly={formData2.unlimited}
                       className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                         formData2.unlimited ? 'bg-gray-100 cursor-not-allowed' : ''
                       }`}
                       placeholder={formData2.unlimited ? '0' : '100'}
                     />
                     <div className="mt-2 flex items-center">
                        <input
                          type="checkbox"
                          id="unlimited2"
                          checked={formData2.unlimited && formData2.unlimited !== 0}
                          onChange={(e) => {
                            setFormData2({
                              ...formData2,
                              unlimited: e.target.checked,
                              quantity: e.target.checked ? '' : formData2.quantity
                            });
                            if (error2) setError2('');
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                       <label htmlFor="unlimited2" className="ml-2 block text-sm text-gray-700">
                         Unlimited
                       </label>
                     </div>
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Status
                     </label>
                     <select
                       value={formData2.status}
                       onChange={(e) => setFormData2({...formData2, status: e.target.value})}
                       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                     >
                       {accessCodeStatuses.map(status => (
                         <option key={status.value} value={status.value}>
                           {status.label}
                         </option>
                       ))}
                     </select>
                   </div>
                 </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal2}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting2}
                  >
                    {submitting2 ? 'Submitting...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessCodes;
